import { useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import {
  type ActiveEvent,
  type Config,
  type GameConfig
} from "kadow-jetpack-model";
import {
  updateJetpackConfigThunk,
  useJetpackConfigSelector
} from "../../../../../redux/jetpackConfig";
import { useAccountToDisplaySelector } from "../../../../../redux/actions/user";
import { useSubscriptionPlansSelector } from "../../../../../redux/actions/app";
import { tt } from "../../../../../utils/i18n/i18n";
import { type GameListItem } from "./gameListTypes";

/*
  Display order is 1-based: the backend normalises a falsy "o" to 1, so the first
  card must start at 1 to stay consistent with that default.
*/
const ORDER_START = 1;

interface ReorderableGameList {
  games: GameConfig[];
  activeEvents: ActiveEvent[];
  canDeleteGames: boolean;
  commitOrder: (orderedItems: GameListItem[]) => void;
  deleteGame: (game: GameConfig) => void;
  deleteEvent: (ae: ActiveEvent) => void;
}

function sameEvent(a: ActiveEvent, b: ActiveEvent): boolean {
  return (
    a.id === b.id &&
    a.sts === b.sts &&
    a.ets === b.ets &&
    a.gameType === b.gameType
  );
}

export function useReorderableGameList(): ReorderableGameList {
  const config = useJetpackConfigSelector();
  const dispatch = useDispatch();
  const { accountToDisplay } = useAccountToDisplaySelector();
  const plans = useSubscriptionPlansSelector();

  const [games, setGames] = useState<GameConfig[]>(config?.games || []);
  const [activeEvents, setActiveEvents] = useState<ActiveEvent[]>(
    config?.activeEvents || []
  );
  const [disabledEvents, setDisabledEvents] = useState<ActiveEvent[]>(
    config?.dse || []
  );

  /*
    The store is only updated once the server answers updateJetpackConfigThunk, so
    we mirror the config locally and mutate it optimistically. This keeps drag & drop
    and deletions instant (no loader, no blocked UI) while the request is in flight.
  */
  useEffect(() => {
    setGames(config?.games || []);
    setActiveEvents(config?.activeEvents || []);
    setDisabledEvents(config?.dse || []);
  }, [config?.games, config?.activeEvents, config?.dse]);

  const plan = useMemo(() => {
    return plans?.find((p) => p._id === accountToDisplay?.saasPlanId) ?? null;
  }, [plans, accountToDisplay]);

  const canDeleteGames = useMemo(() => {
    return !!plan?.price && plan.price > 0;
  }, [plan]);

  const persist = (update: Partial<Config>) => {
    dispatch(
      updateJetpackConfigThunk(
        update,
        accountToDisplay?.trackingID,
        accountToDisplay?.token
      )
    );
  };

  const commitOrder = (orderedItems: GameListItem[]) => {
    /*
      Games and events share a single ordering space, so we stamp every dragged item
      with its global position ("o") and let the list sort on it. This is what lets a
      game and an event be interleaved even though they live in two separate arrays.
    */
    const includedGames = new Set<GameConfig>();
    const includedActiveEvents = new Set<ActiveEvent>();
    const orderedGames: GameConfig[] = [];
    const orderedActiveEvents: ActiveEvent[] = [];

    orderedItems.forEach((item, index) => {
      const order = index + ORDER_START;
      if (item.type === "game") {
        includedGames.add(item.game);
        orderedGames.push({ ...item.game, o: order });
      } else {
        includedActiveEvents.add(item.ae);
        orderedActiveEvents.push({ ...item.ae, o: order });
      }
    });

    /*
      Items that are filtered out before reaching the list (hidden games, events
      without a renderable skin, etc) are appended so their config entries survive
      a reorder instead of being silently dropped.
    */
    const newGames = [
      ...orderedGames,
      ...games.filter((g) => !includedGames.has(g))
    ];
    const newActiveEvents = [
      ...orderedActiveEvents,
      ...activeEvents.filter((ae) => !includedActiveEvents.has(ae))
    ];

    setGames(newGames);
    setActiveEvents(newActiveEvents);
    persist({ games: newGames, activeEvents: newActiveEvents });
  };

  const deleteGame = (game: GameConfig) => {
    if (!confirm(tt("confirm_deletion"))) {
      return;
    }

    const newGames = games.filter((g) => g.name !== game.name);
    setGames(newGames);
    persist({ games: newGames });
  };

  const deleteEvent = (ae: ActiveEvent) => {
    if (!confirm(tt("confirm_deletion"))) {
      return;
    }

    const newActiveEvents = activeEvents.filter((e) => !sameEvent(e, ae));

    /*
      Scheduled events are re-added by the batch, so instead of dropping them we move
      them to config.dse (disabled scheduled events). The batch skips anything listed
      there, which keeps a disabled scheduled game hidden from display. Manual events
      are simply removed.
    */
    if (ae.s) {
      const newDisabled = [...disabledEvents, ae];
      setActiveEvents(newActiveEvents);
      setDisabledEvents(newDisabled);
      persist({ activeEvents: newActiveEvents, dse: newDisabled });
    } else {
      setActiveEvents(newActiveEvents);
      persist({ activeEvents: newActiveEvents });
    }
  };

  return {
    games,
    activeEvents,
    canDeleteGames,
    commitOrder,
    deleteGame,
    deleteEvent
  };
}
